<template>
  <div dir="rtl" class="ur-wrap">

    <!-- Hero -->
    <div class="ur-hero">
      <div class="ur-hero-text">
        <h1 class="ur-title">
          <template v-if="clientName">שלום {{ clientName }},<br></template>
          בוא נשלח את המסמכים
          <span class="ur-rep-name">ל{{ repName }}</span>
        </h1>
        <p class="ur-desc">
          <template v-if="requiredDone === requiredTotal">
            כל מסמכי החובה כאן — אפשר לשלוח את המסמכים.
          </template>
          <template v-else>
            נשארו {{ requiredTotal - requiredDone }} מסמכים שצריך כדי להגיש את הדוח. גרור, צלם או בחר — לוקח פחות מ-5 דקות.
          </template>
        </p>
      </div>
      <div class="ur-donut-wrap">
        <svg width="140" height="140" class="ur-donut-svg">
          <circle cx="70" cy="70" r="56" fill="none" stroke="#E2E8F0" stroke-width="10"/>
          <circle cx="70" cy="70" r="56" fill="none"
            stroke="#1E5BD8" stroke-width="10"
            stroke-linecap="round"
            :stroke-dasharray="donutC"
            :stroke-dashoffset="donutOffset"
            transform="rotate(-90 70 70)"
            class="ur-donut-arc"/>
        </svg>
        <div class="ur-donut-center">
          <div class="ur-donut-count">{{ done }}<span class="ur-donut-of">/{{ total }}</span></div>
          <div class="ur-donut-label">הועלו</div>
        </div>
      </div>
    </div>

    <!-- Section header + tabs -->
    <div class="ur-sec-header">
      <h2 class="ur-sec-title">רשימת המסמכים</h2>
      <div class="ur-divider"></div>
      <div class="ur-tabs">
        <button
          v-for="t in tabs"
          :key="t.id"
          class="ur-tab"
          :class="{ 'ur-tab-active': activeTab === t.id }"
          @click="activeTab = t.id">
          {{ t.label }}
          <span class="ur-tab-count" :class="{ 'ur-tab-count-active': activeTab === t.id }">{{ t.count }}</span>
        </button>
      </div>
    </div>

    <!-- Doc list -->
    <div class="ur-doc-list">
      <div
        v-for="(doc, i) in filteredDocs"
        :key="doc.id"
        class="ur-doc-row"
        :class="{ 'ur-doc-done': isDone(doc.id) }"
        :style="{ borderBottom: i < filteredDocs.length - 1 ? '1px solid #E2E8F0' : 'none' }">

        <!-- Doc header -->
        <div class="ur-doc-head">
          <div class="ur-doc-num" :class="{ 'ur-doc-num-done': isDone(doc.id) }">
            <svg v-if="isDone(doc.id)" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
              <path d="M5 12l4 4L19 7"/>
            </svg>
            <template v-else>{{ docIndex(doc.id) }}</template>
          </div>
          <div class="ur-doc-info">
            <div class="ur-doc-title-row">
              <span v-if="doc.code" class="ur-code-badge">{{ doc.code }}</span>
              <h3 class="ur-doc-title" :class="{ 'ur-doc-title-done': isDone(doc.id) }">{{ doc.title }}</h3>
              <span v-if="doc.required" class="ur-required-badge">חובה</span>
            </div>
            <div class="ur-doc-sub">
              <template v-if="isDone(doc.id)">
                <span class="ur-uploaded-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="13" height="13">
                    <path d="M5 12l4 4L19 7"/>
                  </svg>
                  {{ uploadedLabel(doc.id) }}
                </span>
              </template>
              <template v-else>{{ doc.sub }}</template>
            </div>
          </div>
          <!-- '+' button — only shown when doc has files and not locked -->
          <button
            v-if="isDone(doc.id) && !closed"
            class="ur-plus-btn"
            :class="{ 'ur-plus-active': adding[doc.id] }"
            :title="adding[doc.id] ? 'סגור' : 'הוסף מסמך נוסף'"
            @click="toggleAdd(doc.id)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>
        </div>

        <!-- Doc body -->
        <div class="ur-doc-body">

          <!-- Upload spinner overlay -->
          <div v-if="uploadingDocId === doc.id" class="ur-uploading">
            <svg class="ur-spin" viewBox="0 0 24 24" fill="none" stroke="#1E5BD8" stroke-width="2" width="24" height="24">
              <circle cx="12" cy="12" r="10" stroke-opacity="0.2"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
            </svg>
            מעלה מסמך...
          </div>

          <!-- Upload zone (empty docs, or when + is active) -->
          <div
            v-if="showAddZone(doc.id)"
            class="ur-upload-zone"
            :class="{
              'ur-upload-compact': isDone(doc.id),
              'ur-upload-hot': !isMobile && dragOverDocId === doc.id
            }"
            v-on="isMobile ? {} : {
              dragenter: (e) => { e.preventDefault(); dragOverDocId = doc.id },
              dragleave: (e) => { e.preventDefault(); dragOverDocId = null },
              dragover:  (e) => e.preventDefault(),
              drop:      (e) => { e.preventDefault(); handleDrop(e, doc.id) }
            }"
            @click="triggerUpload(doc.id)">
            <input
              :ref="'fileInput_' + doc.id"
              type="file"
              class="ur-file-input"
              accept="image/*,.pdf"
              multiple
              @change="handleFileSelect($event, doc.id)"
              @click.stop/>
            <div class="ur-upload-icon">
              <svg v-if="isDone(doc.id)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="20" height="20">
                <path d="M12 5v14M5 12h14"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="22" height="22">
                <path d="M12 16V4M12 4l-4 4M12 4l4 4"/><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>
              </svg>
            </div>
            <div class="ur-upload-text">
              <div class="ur-upload-title">
                <template v-if="isDone(doc.id)">הוסף מסמך נוסף לקטגוריה זו</template>
                <template v-else-if="isMobile">
                  <span class="ur-link">בחר קובץ מהגלריה או צלם</span>
                </template>
                <template v-else>גרור קובץ לכאן או <span class="ur-link">בחר מהמחשב</span></template>
              </div>
              <div class="ur-upload-hint">
                PDF · JPG · PNG · עד 10MB
                <template v-if="isDone(doc.id)"> · מתאים אם יש כמה עמודים או מנפיקים</template>
              </div>
            </div>
          </div>

          <!-- Locked + missing -->
          <div v-if="closed && !isDone(doc.id)" class="ur-locked-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">
              <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>
            </svg>
            הבקשה נסגרה — לא הועלה מסמך זה
          </div>

          <!-- Uploaded files (always visible) -->
          <div v-for="file in filesOf(doc.id)" :key="file.id" class="ur-file-row">
            <div class="ur-file-main">
              <!-- Icon -->
              <div class="ur-file-icon">
                <svg v-if="file.kind === 'img'" viewBox="0 0 24 24" fill="none" stroke="#1E5BD8" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="36" height="36">
                  <rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="M21 16l-5-5-7 7"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" width="36" height="36">
                  <path d="M6 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6H6Z" fill="#FEE2E2" stroke="#DC2626" stroke-width="1.4"/>
                  <path d="M14 3v5a1 1 0 0 0 1 1h5" stroke="#DC2626" stroke-width="1.4" fill="none"/>
                  <text x="12" y="17" text-anchor="middle" font-size="5.5" font-weight="800" fill="#DC2626" font-family="Heebo, sans-serif">PDF</text>
                </svg>
              </div>
              <!-- Info -->
              <div class="ur-file-info">
                <div class="ur-file-name">{{ file.name }}</div>
                <div class="ur-file-meta">
                  <template v-if="file.size">{{ file.size }} · </template>
                  הועלה {{ file.when }}
                </div>
              </div>
              <!-- Confirm delete -->
              <div v-if="isConfirmDelete(doc.id, file.id)" class="ur-confirm-delete">
                <span class="ur-confirm-text">למחוק?</span>
                <button class="ur-btn-danger" :disabled="!!deletingFileId" @click="removeFile(doc.id, file)">
                  {{ deletingFileId === file.id ? '...' : 'מחק' }}
                </button>
                <button class="ur-btn-sm" @click="confirmDeleteId = null">ביטול</button>
              </div>
              <!-- Actions -->
              <div v-else class="ur-file-actions">
                <button class="ur-icon-btn" title="צפה" @click="viewFile(doc, file)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
                <button
                  v-if="!closed"
                  class="ur-icon-btn"
                  :class="{ 'ur-icon-btn-active': ownerOpenId === file.id }"
                  title="שיוך"
                  @click="toggleOwner(file.id)">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">
                    <circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>
                  </svg>
                </button>
                <button
                  v-if="!closed"
                  class="ur-icon-btn ur-icon-btn-danger"
                  title="מחק"
                  @click="confirmDeleteId = { docId: doc.id, fileId: file.id }">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">
                    <path d="M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13"/>
                  </svg>
                </button>
              </div>
            </div>
            <!-- Owner strip (one open at a time, auto-opens on new upload) -->
            <div v-if="ownerOpenId === file.id" class="ur-owner-strip">
              <span class="ur-owner-label">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="13" height="13">
                  <circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>
                </svg>
                שייך
              </span>
              <div class="ur-owner-options">
                <button
                  v-for="o in ownerOptions"
                  :key="o.id"
                  class="ur-owner-btn"
                  :class="{ 'ur-owner-btn-active': file.owner === o.id }"
                  @click="file.owner = o.id">
                  {{ o.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Submit panel ── -->

    <!-- Closed (after finish) -->
    <div v-if="closed" class="ur-submit-closed">
      <div class="ur-submit-icon-box" style="background:#fff;box-shadow:0 1px 2px rgba(15,157,88,.2)">
        <svg viewBox="0 0 24 24" fill="#0F9D58" width="30" height="30">
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.7 7.7-5.5 5.5a1 1 0 0 1-1.4 0L7 12.4a1 1 0 1 1 1.4-1.4l2.1 2.1 4.8-4.8a1 1 0 0 1 1.4 1.4Z"/>
        </svg>
      </div>
      <div class="ur-submit-body">
        <div class="ur-submit-title" style="color:#0A6B3D">הבקשה נסגרה ונשלחה ל{{ repName }}</div>
        <div class="ur-submit-sub">{{ totalFiles }} מסמכים נשלחו · {{ repName }} תיצור איתך קשר אם יחסר משהו.</div>
      </div>
    </div>

    <!-- Card states (confirm / idle) -->
    <div v-else class="ur-submit-card" :style="submitCardStyle">

      <!-- Confirm clean (all required uploaded) -->
      <template v-if="submitStage === 'confirm-clean'">
        <div class="ur-submit-confirm-head">
          <div class="ur-submit-icon-box" style="background:#E6F5EC;box-shadow:none">
            <svg viewBox="0 0 24 24" fill="#0F9D58" width="26" height="26">
              <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.7 7.7-5.5 5.5a1 1 0 0 1-1.4 0L7 12.4a1 1 0 1 1 1.4-1.4l2.1 2.1 4.8-4.8a1 1 0 0 1 1.4 1.4Z"/>
            </svg>
          </div>
          <div class="ur-submit-body">
            <div class="ur-submit-title">לסגור ולשלוח ל{{ repName }}?</div>
            <div class="ur-submit-sub">כל {{ requiredTotal }} מסמכי החובה הועלו. לאחר הסגירה לא ניתן יהיה להוסיף מסמכים דרך הקישור.</div>
          </div>
        </div>
        <div class="ur-submit-actions">
          <button class="ur-btn-hero ur-btn-success" :disabled="isSending" @click="handleFinish">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M5 12l4 4L19 7"/></svg>
            {{ isSending ? 'שולח...' : 'כן, סגור ושלח' }}
          </button>
          <button class="ur-btn-sm ur-btn-lg" @click="submitStage = 'idle'">ביטול</button>
        </div>
      </template>

      <!-- Confirm missing (some required docs not uploaded) -->
      <template v-else-if="submitStage === 'confirm-missing'">
        <div class="ur-submit-confirm-head">
          <div class="ur-submit-icon-box" style="background:#FEF4DC;box-shadow:none">
            <svg viewBox="0 0 24 24" fill="none" stroke="#C77700" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="26" height="26">
              <circle cx="12" cy="12" r="9"/><path d="M12 8v.01M11 12h1v5h1"/>
            </svg>
          </div>
          <div class="ur-submit-body">
            <div class="ur-submit-title">טרם הועלו כל מסמכי החובה</div>
            <div class="ur-submit-sub">
              חסרים {{ missingRequired.length }} מתוך {{ requiredTotal }} מסמכי חובה. אם תסגור עכשיו, {{ repName }} תקבל את הבקשה ללא המסמכים הבאים:
            </div>
            <div class="ur-missing-pills">
              <span v-for="d in missingRequired" :key="d.id" class="ur-missing-pill">
                <span class="ur-missing-dot"></span>
                {{ d.code ? `${d.code} · ` : '' }}{{ d.title }}
              </span>
            </div>
          </div>
        </div>
        <div class="ur-submit-actions">
          <button class="ur-btn-hero" @click="submitStage = 'idle'">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="17" height="17">
              <path d="M12 16V4M12 4l-4 4M12 4l4 4"/><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>
            </svg>
            חזור להעלאת המסמכים החסרים
          </button>
          <button class="ur-btn-sm ur-btn-lg ur-btn-warn" :disabled="isSending" @click="handleFinish">
            {{ isSending ? 'שולח...' : 'סגור בכל זאת ושלח את מה שיש' }}
          </button>
        </div>
      </template>

      <!-- Idle -->
      <template v-else>
        <!-- Sent banner (link stays active) -->
        <div v-if="wasSent" class="ur-sent-banner">
          <svg viewBox="0 0 24 24" fill="#0F9D58" width="18" height="18">
            <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.7 7.7-5.5 5.5a1 1 0 0 1-1.4 0L7 12.4a1 1 0 1 1 1.4-1.4l2.1 2.1 4.8-4.8a1 1 0 0 1 1.4 1.4Z"/>
          </svg>
          <div class="ur-sent-banner-text">המסמכים נשלחו ל{{ repName }} · הקישור עדיין פעיל — אפשר להוסיף עוד ולשלוח שוב</div>
        </div>
        <!-- Idle body -->
        <div class="ur-idle-body">
          <div class="ur-submit-icon-box" :style="{ background: allRequired ? '#E6F5EC' : '#EEF3FE', boxShadow: 'none' }">
            <svg v-if="allRequired" viewBox="0 0 24 24" fill="#0F9D58" width="28" height="28">
              <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm4.7 7.7-5.5 5.5a1 1 0 0 1-1.4 0L7 12.4a1 1 0 1 1 1.4-1.4l2.1 2.1 4.8-4.8a1 1 0 0 1 1.4 1.4Z"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="#1E5BD8" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="24" height="24">
              <path d="M12 16V4M12 4l-4 4M12 4l4 4"/><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3"/>
            </svg>
          </div>
          <div class="ur-submit-body">
            <div class="ur-submit-title">
              {{ allRequired ? `הכל מוכן — אפשר לשלוח ל${repName}` : `שלח ל${repName} מה שכבר העלית` }}
            </div>
            <div class="ur-submit-sub">
              <template v-if="allRequired">כל מסמכי החובה הועלו. שלח עכשיו, או סיים וסגור את הבקשה.</template>
              <template v-else>אין צורך לאסוף הכל עכשיו — שלח את {{ totalFiles }} המסמכים שהעלית, והקישור יישאר פעיל כדי שתחזור ותשלים.</template>
            </div>
          </div>
          <div class="ur-idle-btns">
            <button
              class="ur-btn-hero ur-btn-primary"
              :disabled="isSending || totalFiles === 0"
              @click="handleSend">
              {{ isSending ? 'שולח...' : wasSent ? 'שלח מסמכים שנוספו' : 'שלח לרו״ח' }}
              <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18"><path d="M15 6l-6 6 6 6"/></svg>
            </button>
            <button class="ur-btn-sm ur-btn-finish" @click="startFinish">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15"><path d="M5 12l4 4L19 7"/></svg>
              סיימתי להעלות, סגור בקשה
            </button>
          </div>
        </div>
        <div class="ur-submit-footer">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="13" height="13">
            <rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>
          </svg>
          הקבצים מאובטחים בהצפנה · שמירה אוטומטית — תוכל לחזור לקישור ולהמשיך מאיפה שעצרת
        </div>
      </template>
    </div>

    <!-- File preview modal -->
    <transition name="ur-fade">
      <div v-if="preview" class="ur-modal-backdrop" @click="closePreview">
        <div class="ur-modal ur-modal-large" dir="rtl" @click.stop>
          <div class="ur-modal-header">
            <div class="ur-modal-icon">
              <svg v-if="preview.file.kind === 'img'" viewBox="0 0 24 24" fill="none" stroke="#1E5BD8" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="28" height="28">
                <rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="1.6"/><path d="M21 16l-5-5-7 7"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" width="28" height="28">
                <path d="M6 3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6H6Z" fill="#FEE2E2" stroke="#DC2626" stroke-width="1.4"/>
                <path d="M14 3v5a1 1 0 0 0 1 1h5" stroke="#DC2626" stroke-width="1.4" fill="none"/>
                <text x="12" y="17" text-anchor="middle" font-size="5.5" font-weight="800" fill="#DC2626" font-family="Heebo, sans-serif">PDF</text>
              </svg>
            </div>
            <div class="ur-modal-title-group">
              <div class="ur-modal-title">{{ preview.file.name }}</div>
              <div class="ur-modal-sub">{{ preview.doc.title }}</div>
            </div>
            <a v-if="previewBlobUrl" :href="previewBlobUrl" target="_blank" class="ur-btn-sm" style="flex:none;font-size:12px">פתח בכרטיסייה חדשה</a>
            <button class="ur-icon-btn" style="width:34px;height:34px;flex:none" @click="closePreview">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
                <path d="M6 6l12 12M18 6L6 18"/>
              </svg>
            </button>
          </div>
          <!-- Loading (API fetch for previous-session files) -->
          <div v-if="previewLoading" class="ur-modal-loading">
            <svg class="ur-spin" viewBox="0 0 24 24" fill="none" stroke="#1E5BD8" stroke-width="2" width="32" height="32">
              <circle cx="12" cy="12" r="10" stroke-opacity="0.2"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
            </svg>
            טוען קובץ...
          </div>
          <!-- Image viewer -->
          <div v-else-if="previewBlobUrl && preview.file.kind === 'img'" class="ur-modal-img-body">
            <img :src="previewBlobUrl" class="ur-preview-img" :alt="preview.file.name" />
          </div>
          <!-- PDF viewer -->
          <div v-else-if="previewBlobUrl && preview.file.kind !== 'img'" class="ur-modal-pdf-body">
            <iframe :src="previewBlobUrl + '#toolbar=0&navpanes=0&view=FitH'" class="ur-preview-iframe" :title="preview.file.name"></iframe>
          </div>
          <!-- No local file (uploaded in a previous session) -->
          <div v-else-if="previewError" class="ur-modal-error">
            <p style="color:#64748B;font-size:14px">תצוגה מקדימה זמינה רק לקבצים שהועלו בסשן הנוכחי.</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import DocumentService from '@/services/DocumentService.js'

const OWNER_OPTIONS = [
  { id: 'registered', label: 'לי' },
  { id: 'spouse',     label: 'לבן/בת זוגי' },
  { id: 'both',       label: 'לשנינו' },
]

export default {
  name: 'UploadRequest',
  props: {
    uploadAttributes: { type: Array, default: () => [] },
    documentShortUrl: { type: String, required: true },
    documentToken:    { type: String, required: true },
    repName:          { type: String, default: '' },
    clientName:       { type: String, default: '' },
    isSending:        { type: Boolean, default: false },
    wasSent:          { type: Boolean, default: false },
  },
  emits: ['send', 'finish'],

  data() {
    return {
      ownerOptions: OWNER_OPTIONS,
      fileState:       {},
      activeTab:       'all',
      adding:          {},
      ownerOpenId:     null,
      closed:          false,
      preview:         null,
      previewBlobUrl:  null,
      previewError:    false,
      previewLoading:  false,
      fileSeq:         100,
      dragOverDocId:   null,
      confirmDeleteId: null,
      submitStage:     'idle',
      uploadingDocId:  null,
      deletingFileId:  null,
    }
  },

  computed: {
    docs() {
      // Group by fileDocumentType: multiple attributes for the same document code
      // (e.g. salary/owner=1 and salary/owner=2) produce a single row in the UI.
      // The first attribute for each type supplies the display metadata.
      // fileDocumentOwner on the doc is used only as the default owner when uploading;
      // per-file owner assignment is tracked in fileState and resolved at sign time.
      const seen = new Set()
      const result = []
      for (const attr of this.uploadAttributes) {
        const key = String(attr.fileDocumentType)
        if (seen.has(key)) continue
        seen.add(key)
        result.push({
          id:               key,
          title:            attr.fileDocumentName || key,
          sub:              attr.description || attr.sub || '',
          required:         !!attr.required,
          fileDocumentType: attr.fileDocumentType,
          fileDocumentOwner: attr.fileDocumentOwner,
        })
      }
      return result
    },
    done()          { return this.docs.filter(d => this.isDone(d.id)).length },
    total()         { return this.docs.length },
    requiredDone()  { return this.docs.filter(d => d.required && this.isDone(d.id)).length },
    requiredTotal() { return this.docs.filter(d => d.required).length },
    pct()           { return this.total ? Math.round((this.done / this.total) * 100) : 0 },
    allRequired()   { return this.requiredDone === this.requiredTotal },
    missingRequired() { return this.docs.filter(d => d.required && !this.isDone(d.id)) },
    totalFiles()    { return this.docs.reduce((n, d) => n + this.filesOf(d.id).length, 0) },
    donutC()        { return 2 * Math.PI * 56 },
    donutOffset()   { const c = this.donutC; return c - (c * this.pct) / 100 },

    filteredDocs() {
      return this.docs.filter(d => {
        if (this.activeTab === 'all')      return true
        if (this.activeTab === 'required') return d.required
        if (this.activeTab === 'done')     return this.isDone(d.id)
        if (this.activeTab === 'missing')  return !this.isDone(d.id)
        return true
      })
    },
    tabs() {
      return [
        { id: 'all',      label: 'הכל',   count: this.docs.length },
        { id: 'required', label: 'חובה',  count: this.requiredTotal },
        { id: 'missing',  label: 'חסרים', count: this.total - this.done },
        { id: 'done',     label: 'הועלו', count: this.done },
      ]
    },
    submitCardStyle() {
      if (this.submitStage === 'confirm-clean') {
        return { border: '1px solid #C6D6F8', boxShadow: '0 4px 20px rgba(30,91,216,.08)' }
      }
      if (this.submitStage === 'confirm-missing') {
        return { border: '1px solid #F0D69A', boxShadow: '0 4px 20px rgba(199,119,0,.08)' }
      }
      return {}
    },
    isMobile() {
      return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent)
        || ('ontouchstart' in window && window.innerWidth < 1024)
    },
  },

  watch: {
    uploadAttributes: {
      immediate: true,
      handler(attrs) {
        const state = {}
        for (const attr of attrs) {
          const docId = String(attr.fileDocumentType)
          const localState = this.fileState[docId]
          if (localState) {
            // Preserve any local interaction (uploads this session) for this doc type
            state[docId] = localState
          } else {
            // Fresh load: accumulate files from ALL attributes sharing this type.
            // Each file gets the owner from its source attribute, not a hardcoded default.
            if (!state[docId]) state[docId] = { files: [] }
            const incoming = (attr.files || []).map(f => ({
              id:       f.fileGuid,
              name:     f.fileName || f.name || '',
              size:     '',
              when:     'קודם',
              kind:     this.guessKind(f.fileName || f.name || ''),
              owner:    this.ownerFromInt(attr.fileDocumentOwner),
              fileGuid: f.fileGuid,
            }))
            state[docId].files.push(...incoming)
          }
        }
        this.fileState = state
      }
    }
  },

  methods: {
    filesOf(docId)    { return this.fileState[docId]?.files || [] },
    isDone(docId)     { return this.filesOf(docId).length > 0 },
    docIndex(docId)   { return this.docs.findIndex(d => d.id === docId) + 1 },
    showAddZone(docId) { return !this.closed && !this.uploadingDocId && (!this.isDone(docId) || !!this.adding[docId]) },
    isConfirmDelete(docId, fileId) {
      return this.confirmDeleteId?.docId === docId && this.confirmDeleteId?.fileId === fileId
    },
    uploadedLabel(docId) {
      const files = this.filesOf(docId)
      return files.length === 1 ? files[0].name : `${files.length} קבצים הועלו`
    },
    guessKind(name) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(name || '') ? 'img' : 'pdf'
    },
    ownerFromInt(v) {
      if (v === 2) return 'spouse'
      if (v === 3) return 'both'
      return 'registered'
    },
    ownerToInt(id) {
      if (id === 'spouse') return 2
      if (id === 'both')   return 3
      return 1
    },

    toggleAdd(docId) { this.adding = { ...this.adding, [docId]: !this.adding[docId] } },

    triggerUpload(docId) {
      const ref = this.$refs['fileInput_' + docId]
      const el = Array.isArray(ref) ? ref[0] : ref
      if (el) el.click()
    },

    handleFileSelect(e, docId) {
      const files = e.target.files
      if (!files?.length) return
      Array.from(files).forEach(file => this._uploadFile(docId, file))
      e.target.value = ''
    },

    handleDrop(e, docId) {
      this.dragOverDocId = null
      const files = e.dataTransfer?.files
      if (files?.length) {
        Array.from(files).forEach(file => this._uploadFile(docId, file))
      }
    },

    async _uploadFile(docId, file) {
      const doc = this.docs.find(d => d.id === docId)
      if (!doc) return
      const formData = new FormData()
      formData.append(file.name, file)
      this.uploadingDocId = docId
      try {
        const ownerInt = doc.fileDocumentOwner || 1
        const res = await DocumentService.uploadFile(
          this.documentShortUrl, this.documentToken,
          doc.fileDocumentType, ownerInt, formData
        )
        const uploaded = Array.isArray(res.data)
          ? (res.data.find(f => f.fileName === file.name) || res.data[res.data.length - 1])
          : res.data
        if (uploaded) {
          const n = ++this.fileSeq
          const newId = uploaded.fileGuid || 'f' + n
          const docState = this.fileState[docId] || { files: [] }
          this.fileState = {
            ...this.fileState,
            [docId]: {
              files: [
                ...docState.files,
                {
                  id:         newId,
                  name:       uploaded.fileName || file.name,
                  size:       file.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : '',
                  when:       'הרגע',
                  kind:       this.guessKind(file.name),
                  owner:      'registered', // default me=1
                  fileGuid:   uploaded.fileGuid,
                  fileObject: file, // keep browser File in memory for client-side preview
                }
              ]
            }
          }
          this.adding = { ...this.adding, [docId]: false }
          this.ownerOpenId = newId
        }
      } catch (err) {
        const code = err.response?.data?.errorCode
        alert(code === '19' || code === 19
          ? 'חרגת מכמות הקבצים המקסימלית המותרת'
          : 'קרתה תקלה בהעלאה, אנא נסה שנית.')
      } finally {
        this.uploadingDocId = null
      }
    },

    async removeFile(docId, file) {
      const doc = this.docs.find(d => d.id === docId)
      if (!doc) return
      this.deletingFileId = file.id
      try {
        await DocumentService.deleteFile(
          this.documentShortUrl, this.documentToken,
          file.fileGuid || file.id,
          doc.fileDocumentOwner || 1
        )
        this.fileState = {
          ...this.fileState,
          [docId]: { files: this.fileState[docId].files.filter(f => f.id !== file.id) }
        }
        this.confirmDeleteId = null
      } catch {
        alert('קרתה תקלה במחיקה, אנא נסה שנית.')
      } finally {
        this.deletingFileId = null
      }
    },

    setOwner(docId, fileId, owner) {
      this.fileState = {
        ...this.fileState,
        [docId]: { files: this.fileState[docId].files.map(f => f.id === fileId ? { ...f, owner } : f) }
      }
    },

    toggleOwner(fileId) { this.ownerOpenId = this.ownerOpenId === fileId ? null : fileId },

    async viewFile(doc, file) {
      this.preview = { doc, file }
      this.previewBlobUrl = null
      this.previewError = false

      if (file.fileObject) {
        // Current session: use the browser File object directly — instant, no API call
        this.previewBlobUrl = URL.createObjectURL(file.fileObject)
      } else if (file.fileGuid) {
        // Previous session: fetch from server using the guid
        this.previewLoading = true
        try {
          const res = await DocumentService.getFile(this.documentShortUrl, this.documentToken, file.fileGuid)
          const mimeType = file.kind === 'img' ? 'image/jpeg' : 'application/pdf'
          const blob = new Blob([res.data], { type: mimeType })
          this.previewBlobUrl = URL.createObjectURL(blob)
        } catch {
          this.previewError = true
        } finally {
          this.previewLoading = false
        }
      } else {
        this.previewError = true
      }
    },

    closePreview() {
      if (this.previewBlobUrl) {
        URL.revokeObjectURL(this.previewBlobUrl)
        this.previewBlobUrl = null
      }
      this.preview = null
      this.previewError = false
    },

    buildOwnerPayload() {
      const result = []
      this.docs.forEach(doc => {
        this.filesOf(doc.id).forEach(file => {
          result.push({
            fileDocumentType:  doc.fileDocumentType,
            fileGuid:          file.fileGuid || file.id,
            fileDocumentOwner: this.ownerToInt(file.owner),
          })
        })
      })
      return result
    },

    startFinish() { this.submitStage = this.allRequired ? 'confirm-clean' : 'confirm-missing' },

    handleSend() {
      this.$emit('send', this.buildOwnerPayload())
    },

    handleFinish() {
      this.$emit('finish', this.buildOwnerPayload())
      this.closed = true
    },

    handleKeyDown(e) {
      if (e.key === 'Escape' && this.preview) this.closePreview()
    },
  },

  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown)
  },
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');
</style>

<style scoped>
.ur-wrap {
  font-family: 'Heebo', system-ui, sans-serif;
  color: #0F172A;
  padding: 24px 24px 40px;
  max-width: 820px;
  margin: 0 auto;
  flex: 1;
  width: 100%;
}
.ur-wrap *, .ur-wrap *::before, .ur-wrap *::after { box-sizing: border-box; }

/* ── HERO ─────────────────────────────────────── */
.ur-hero { display: flex; align-items: center; gap: 28px; margin-bottom: 28px; }
.ur-hero-text { flex: 1; text-align: right; }
.ur-title { margin: 0; font-size: 32px; font-weight: 800; letter-spacing: -0.025em; line-height: 1.2; text-align: right; }
.ur-rep-name {
  background: linear-gradient(120deg, #1E5BD8, #13d0d1);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.ur-desc { margin: 10px 0 0; font-size: 14px; color: #334155; line-height: 1.55; max-width: 520px; text-align: right; }

.ur-donut-wrap { position: relative; flex: none; width: 140px; height: 140px; }
.ur-donut-svg { display: block; }
.ur-donut-arc { transition: stroke-dashoffset 0.5s; }
.ur-donut-center {
  position: absolute; top: 0; left: 0; width: 140px; height: 140px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; line-height: 1;
}
.ur-donut-count { font-size: 26px; font-weight: 800; letter-spacing: -0.03em; }
.ur-donut-of    { color: #94A3B8; font-weight: 600; }
.ur-donut-label { font-size: 11px; color: #64748B; margin-top: 4px; font-weight: 600; }

/* ── SECTION HEADER ───────────────────────────── */
.ur-sec-header { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; margin-bottom: 14px; }
.ur-sec-title  { margin: 0; font-size: 17px; font-weight: 800; letter-spacing: -0.01em; white-space: nowrap; }
.ur-divider    { flex: 1 1 30px; min-width: 30px; height: 1px; background: #E2E8F0; }
.ur-tabs       { display: grid; grid-template-columns: repeat(4, auto); gap: 2px; max-width: 100%; background: #fff; border-radius: 999px; border: 1px solid #E2E8F0; padding: 3px; }
/* narrow (phones): 2 + 2 — חסרים + הועלו drop to a 2nd line together */
@media (max-width: 480px) {
  .ur-tabs { grid-template-columns: repeat(2, 1fr); border-radius: 18px; }
}
/* very narrow: all 4 stacked, one below the other */
@media (max-width: 250px) {
  .ur-tabs { grid-template-columns: 1fr; }
}
.ur-tab {
  padding: 5px 12px; border-radius: 999px; border: none;
  background: transparent; color: #334155; font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: 'Heebo', sans-serif; display: inline-flex; align-items: center; justify-content: center; gap: 5px; transition: all 0.15s;
}
.ur-tab.ur-tab-active { background: #0F172A; color: #fff; }
.ur-tab-count { font-size: 10px; padding: 1px 6px; border-radius: 999px; background: #F8FAFC; color: #64748B; }
.ur-tab-active .ur-tab-count, .ur-tab-count.ur-tab-count-active { background: rgba(255,255,255,.2); color: #fff; }

/* ── DOC LIST ─────────────────────────────────── */
.ur-doc-list { background: #fff; border-radius: 16px; border: 1px solid #E2E8F0; overflow: hidden; box-shadow: 0 1px 2px rgba(15,23,42,.04); }
.ur-doc-row  { background: #fff; }
.ur-doc-done { background: #FBFCFE !important; }
.ur-doc-head { padding: 16px 20px; display: flex; align-items: center; gap: 14px; }
.ur-doc-num {
  width: 32px; height: 32px; border-radius: 10px; flex: none;
  background: #F8FAFC; color: #94A3B8; display: grid; place-items: center;
  border: 1px dashed #CBD5E1; font-weight: 700; font-size: 13px;
}
.ur-doc-num.ur-doc-num-done { background: #0F9D58; color: #fff; border: none; }
.ur-doc-info { flex: 1; min-width: 0; }
.ur-doc-title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 2px; flex-wrap: wrap; }
.ur-code-badge { font-size: 10px; font-weight: 800; color: #64748B; background: #F8FAFC; padding: 2px 6px; border-radius: 4px; font-family: ui-monospace, monospace; letter-spacing: 0.02em; white-space: nowrap; }
.ur-doc-title  { margin: 0; font-size: 15px; font-weight: 700; letter-spacing: -0.01em; color: #0F172A; }
.ur-doc-title.ur-doc-title-done { color: #64748B; }
.ur-required-badge { font-size: 10px; font-weight: 800; color: #DC2626; background: #FDECEC; padding: 2px 7px; border-radius: 4px; letter-spacing: 0.02em; white-space: nowrap; }
.ur-doc-sub    { font-size: 12px; color: #64748B; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: right; direction: rtl; }
.ur-uploaded-label { display: inline-flex; align-items: center; gap: 6px; color: #0F9D58; font-weight: 600; }
.ur-plus-btn {
  width: 34px; height: 34px; border-radius: 10px; flex: none;
  border: 1px solid #E2E8F0; background: #fff; color: #1E5BD8;
  display: grid; place-items: center; cursor: pointer; transition: all 0.15s;
}
.ur-plus-btn.ur-plus-active { border-color: #1E5BD8; background: #1E5BD8; color: #fff; transform: rotate(45deg); }

/* ── DOC BODY ─────────────────────────────────── */
.ur-doc-body { padding: 0 20px 20px; display: flex; flex-direction: column; gap: 8px; }
.ur-file-input { display: none; }
.ur-uploading { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: #EEF3FE; border-radius: 12px; font-size: 13px; color: #1E5BD8; font-weight: 600; }
.ur-spin { animation: ur-spin 1s linear infinite; }
@keyframes ur-spin { to { transform: rotate(360deg); } }

.ur-upload-zone {
  background: #fff; border: 1.5px dashed #CBD5E1; border-radius: 12px;
  padding: 14px 16px; display: flex; align-items: center; gap: 16px; cursor: pointer; transition: all 0.15s;
}
.ur-upload-zone:hover, .ur-upload-zone.ur-upload-hot { background: #EEF3FE; border-color: #1E5BD8; }
.ur-upload-compact { border-color: #C6D6F8; }
.ur-upload-icon { width: 40px; height: 40px; border-radius: 12px; flex: none; background: #EEF3FE; color: #1E5BD8; display: grid; place-items: center; }
.ur-upload-text  { flex: 1; }
.ur-upload-title { font-weight: 700; font-size: 14px; color: #0F172A; }
.ur-link         { color: #1E5BD8; text-decoration: underline; }
.ur-upload-hint  { font-size: 12px; color: #64748B; margin-top: 2px; }

.ur-locked-note {
  border: 1px dashed #CBD5E1; border-radius: 12px; padding: 12px 16px;
  display: flex; align-items: center; gap: 10px; font-size: 13px; color: #94A3B8; background: #FBFCFD;
}

/* ── FILE ROW ─────────────────────────────────── */
.ur-file-row { background: #fff; border-radius: 12px; border: 1px solid #E2E8F0; padding: 12px; display: flex; flex-direction: column; gap: 10px; }
.ur-file-main { display: flex; align-items: center; gap: 12px; }
.ur-file-icon { flex: none; }
.ur-file-info { flex: 1; min-width: 0; }
.ur-file-name { font-weight: 700; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #0F172A; }
.ur-file-meta { font-size: 12px; color: #64748B; margin-top: 1px; }
.ur-file-actions { display: flex; gap: 6px; }
.ur-icon-btn {
  background: #fff; color: #64748B; border: 1px solid #E2E8F0;
  width: 30px; height: 30px; border-radius: 8px; cursor: pointer;
  display: grid; place-items: center; transition: all 0.12s;
}
.ur-icon-btn:hover { border-color: #CBD5E1; color: #334155; }
.ur-icon-btn.ur-icon-btn-active { border-color: #1E5BD8; background: #EEF3FE; color: #1E5BD8; }
.ur-icon-btn.ur-icon-btn-danger { color: #DC2626; }
.ur-icon-btn.ur-icon-btn-danger:hover { background: #FDECEC; border-color: #DC2626; }

.ur-confirm-delete { display: flex; align-items: center; gap: 8px; }
.ur-confirm-text   { font-size: 12px; color: #64748B; }
.ur-btn-danger { color: #fff; background: #DC2626; border: 1px solid #DC2626; padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: 'Heebo', sans-serif; }
.ur-btn-sm    { background: #fff; color: #334155; border: 1px solid #E2E8F0; padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; font-family: 'Heebo', sans-serif; }

/* ── OWNER STRIP ──────────────────────────────── */
.ur-owner-strip   { display: flex; align-items: center; gap: 10px; padding-top: 10px; border-top: 1px dashed #E2E8F0; justify-content: flex-end; }
.ur-owner-label   { font-size: 12px; color: #64748B; font-weight: 600; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.ur-owner-options { display: flex; background: #F8FAFC; border-radius: 999px; border: 1px solid #E2E8F0; padding: 3px; }
.ur-owner-btn { padding: 5px 14px; border-radius: 999px; border: none; background: transparent; color: #334155; font-size: 12px; font-weight: 600; cursor: pointer; font-family: 'Heebo', sans-serif; transition: background 0.15s; }
.ur-owner-btn.ur-owner-btn-active { background: #1E5BD8; color: #fff; }

/* ── SUBMIT PANEL ─────────────────────────────── */
.ur-submit-closed {
  margin-top: 24px; padding: 24px; background: #E6F5EC; border-radius: 16px; border: 1px solid #B7E2C7;
  display: flex; align-items: center; gap: 20px;
}
.ur-submit-icon-box { width: 48px; height: 48px; border-radius: 12px; flex: none; display: grid; place-items: center; }
.ur-submit-body { flex: 1; }
.ur-submit-title { font-weight: 800; font-size: 16px; }
.ur-submit-sub   { font-size: 13px; color: #334155; margin-top: 3px; line-height: 1.5; }

.ur-submit-card {
  margin-top: 24px; background: #fff; border-radius: 16px;
  border: 1px solid #E2E8F0; overflow: hidden; box-shadow: 0 1px 2px rgba(15,23,42,.04);
}
.ur-submit-confirm-head { display: flex; align-items: flex-start; gap: 14px; padding: 24px 24px 0; }
.ur-submit-actions { display: flex; gap: 10px; padding: 16px 24px 24px; justify-content: flex-start; flex-wrap: wrap; }

.ur-btn-hero {
  background: #0F172A; color: #fff; border: none; padding: 13px 22px; border-radius: 12px;
  font-size: 14px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
  font-family: 'Heebo', sans-serif; box-shadow: 0 4px 14px rgba(15,23,42,.18); transition: opacity 0.15s;
}
.ur-btn-hero:disabled { opacity: 0.5; cursor: not-allowed; }
.ur-btn-hero.ur-btn-success { background: #0F9D58; box-shadow: 0 4px 14px rgba(15,157,88,.25); }
.ur-btn-hero.ur-btn-primary { background: #1E5BD8; box-shadow: 0 4px 14px rgba(30,91,216,.25); }
.ur-btn-sm.ur-btn-lg  { padding: 11px 16px !important; font-size: 13px !important; }
.ur-btn-sm.ur-btn-warn { color: #C77700; border-color: #F0D69A; }

.ur-missing-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.ur-missing-pill  { display: inline-flex; align-items: center; gap: 6px; background: #FDECEC; color: #DC2626; font-size: 12px; font-weight: 600; padding: 5px 11px; border-radius: 999px; }
.ur-missing-dot   { width: 6px; height: 6px; border-radius: 999px; background: #DC2626; }

.ur-sent-banner { padding: 12px 20px; background: #E6F5EC; border-bottom: 1px solid #B7E2C7; display: flex; align-items: center; gap: 10px; }
.ur-sent-banner-text { font-size: 13px; color: #0A6B3D; font-weight: 600; flex: 1; }

.ur-idle-body { padding: 22px; display: flex; flex-wrap: wrap; align-items: center; gap: 18px; }
.ur-idle-body .ur-submit-body { flex: 1 1 220px; min-width: min(220px, 100%); }
.ur-idle-btns { display: flex; flex-direction: column; gap: 8px; flex: 1 1 auto; }
.ur-btn-finish { justify-content: center; padding: 9px 14px !important; font-size: 12px !important; }
.ur-submit-footer { padding: 10px 20px; border-top: 1px dashed #E2E8F0; display: flex; align-items: center; gap: 8px; font-size: 12px; color: #64748B; }

/* ── MODAL ────────────────────────────────────── */
.ur-modal-backdrop {
  position: fixed; inset: 0; z-index: 100; background: rgba(15,23,42,.55); backdrop-filter: blur(3px);
  display: grid; place-items: center; padding: 32px;
}
.ur-modal {
  width: min(540px, 92vw); max-height: 90vh; background: #fff; border-radius: 18px; overflow: hidden;
  display: flex; flex-direction: column; box-shadow: 0 24px 60px rgba(15,23,42,.35); font-family: 'Heebo', sans-serif;
}
.ur-modal-header { padding: 14px 18px; border-bottom: 1px solid #E2E8F0; display: flex; align-items: center; gap: 12px; }
.ur-modal-icon { flex: none; }
.ur-modal-title-group { flex: 1; min-width: 0; }
.ur-modal-title { font-weight: 700; font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ur-modal-sub { font-size: 12px; color: #64748B; }
.ur-modal-large { width: min(800px, 96vw) !important; }
.ur-modal-loading { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; min-height: 300px; font-size: 14px; color: #64748B; }
.ur-modal-img-body { flex: 1; overflow: auto; background: #EEF1F6; display: flex; align-items: center; justify-content: center; padding: 16px; }
.ur-preview-img { max-width: 100%; max-height: 75vh; border-radius: 4px; box-shadow: 0 8px 28px rgba(15,23,42,.18); }
.ur-modal-pdf-body { flex: 1; display: flex; min-height: 70vh; }
.ur-preview-iframe { flex: 1; border: none; width: 100%; min-height: 70vh; }
.ur-modal-error { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 32px; text-align: center; color: #64748B; }

/* ── TRANSITION ───────────────────────────────── */
.ur-fade-enter-active, .ur-fade-leave-active { transition: opacity 0.2s; }
.ur-fade-enter-from,   .ur-fade-leave-to     { opacity: 0; }
</style>
